import { createFileRoute, Link } from '@tanstack/react-router'
import { GuideArticle, GuideSection } from '~/components/guide/GuideArticle'

export const Route = createFileRoute('/guide/analytics')({
  component: AnalyticsGuide,
})

function AnalyticsGuide() {
  return (
    <GuideArticle>
      <GuideSection title="Analytics">
        <p>
          The <Link to="/analytics" className="underline underline-offset-2">Analytics</Link>{' '}
          page reports adoption and usage of the dispatch tool over a selectable
          date range (last 7 / 30 / 90 days) with a day/week bucket toggle. It is
          visible to anyone holding the <strong>analytics:read</strong> permission
          (admins always; grant it to other roles in the admin Roles editor).
        </p>
      </GuideSection>

      <GuideSection title="Layout">
        <p>
          The page is organised into four tabs — <strong>Activity</strong>,{' '}
          <strong>Outcomes</strong>, <strong>Coverage</strong> and <strong>ETA sources</strong>. The first
          three sit beneath a shared control row (date range, day/week bucket) and four headline
          cards. <strong>ETA sources</strong> hides those: it has its own dates (up to 31 days) and its
          own headline tiles, because it reads allocation and arrival from EGIS. The Activity tab has three
          sub-tabs: <strong>Dashboard</strong> (activity over time, data usage,
          and the activity funnel), <strong>Users</strong> (most active users),
          and <strong>Recent feed</strong> (the raw event feed with CSV export).
          Tabs are linkable — for example{' '}
          <code>/analytics?tab=activity&amp;sub=feed</code> opens the feed and
          its Export CSV button directly.
        </p>
      </GuideSection>

      <GuideSection title="What it shows">
        <p>
          Headline cards (sessions, active users, assets searched, acceptance
          rate); an activity-over-time chart; on the <strong>Outcomes</strong>{' '}
          tab an acceptance funnel
          (searches → sessions with an outcome → accepted → distinct accepted
          jobs); a data-usage chart of lookups (OK vs error); a most-active-users
          table; and per-officer and per-job tables counting how many sessions
          ended in <em>accepted</em>, <em>declined</em>, or <em>called only</em>.
        </p>
        <p>
          The per-officer/per-job counts are <strong>per session</strong>: each
          search keeps one current outcome per officer/job, so the numbers count
          the sessions that ended in each status — not repeated call attempts
          within a single session.
        </p>
      </GuideSection>

      <GuideSection title="Activity data">
        <p>
          Every dispatch action is recorded as an immutable <strong>activity
          event</strong>: each job-number search, each nearby officer and job
          shown as a recommendation, and each time a recommendation is marked
          called, accepted, declined, or un-marked (&ldquo;cleared&rdquo;). This
          append-only log is the source of truth for measuring success — it keeps
          the full history, including retries and undos, rather than just the
          latest state.
        </p>
        <p>
          The <strong>Activity funnel</strong> card counts those events over the
          selected range (searches → officers/jobs shown → called → accepted /
          declined / cleared), and the <strong>Recent activity events</strong>{' '}
          table lists the raw events newest-first. Each row shows the event&rsquo;s
          related <strong>job</strong>, <strong>service partner</strong>, the{' '}
          <strong>allocated officer</strong> (for nearby-job recommendations) and
          the <strong>property (asset) number</strong>.
        </p>
        <p>
          The <strong>Recent activity events</strong> card (the Activity
          tab&rsquo;s <strong>Recent feed</strong> sub-tab) has its own{' '}
          <strong>From</strong> / <strong>To</strong> date pickers, independent of
          the date range at the top of the page. They default to the last 30 days;{' '}
          <em>clearing</em> a date removes that bound, so emptying both shows all
          time. The <strong>Rows</strong> selector chooses how many events to
          list — 20, 50, 100, or <strong>All</strong>.
        </p>
        <p>
          The table renders at most 500 rows even on <strong>All</strong>. When
          more matched than are shown, a footer says so, and its wording tells you
          how to get the rest: at a numbered <strong>Rows</strong> choice
          (&ldquo;Showing 20 of 8,214 events — choose Rows: All to export
          everything&rdquo;), because <strong>Export CSV</strong> downloads the
          same row count as the table; at <strong>Rows: All</strong>{' '}
          (&ldquo;Showing 500 of 8,214 events — export for the full set&rdquo;),
          because the table has hit its 500-row render cap but the export has not
          — it downloads <em>every</em> matching event with no cap. A wide range
          over a busy period can therefore be a large, slow download.
        </p>
        <p>
          The same data is available over the API at{' '}
          <code>GET /api/v1/analytics/activity</code> (requires{' '}
          <strong>analytics:read</strong>). It is paginated (<code>page</code>,{' '}
          <code>pageSize</code> up to 500) and filterable by{' '}
          <code>eventType</code>, <code>targetType</code>, <code>sessionId</code>,
          and a <code>from</code>/<code>to</code> date range. Add{' '}
          <code>?format=csv</code> to download the events as a CSV file instead of
          the JSON envelope, and <code>?limit=</code> to bound it:{' '}
          <code>limit=all</code> for every matching row, a number for that many
          rows, or omit it for the default 10,000-row cap.
        </p>
      </GuideSection>

      <GuideSection title="Coverage">
        <p>
          The <strong>Coverage</strong> tab measures officer availability from the live
          operative lookups: the average number of officers a search returns,
          how many are within a configurable drive time (default 30 min), and
          how many have updated their location recently (default 10 min, measured
          at the time of the search). Results are grouped by postcode{' '}
          <strong>outcode</strong> (e.g. <em>SW1A</em>), thinnest coverage first,
          so you can see where the country is under-served.
        </p>
        <p>
          Coverage is derived from the postcode returned by the lookup, so it
          only covers searches run after this feature shipped; older searches
          appear under <em>Unknown</em>.
        </p>
      </GuideSection>

      <GuideSection title="ETA sources">
        <p>
          The <strong>ETA sources</strong> tab (<code>/analytics?tab=eta-sources</code>) shows, for each job
          Argus asked about, whether Chase (EGIS), Kestrel, both or neither located the officer between
          allocation and arrival, with each source&apos;s confidence over time and how soon it gave an ETA. It
          has its own date range of up to 31 days, because it reads allocation and arrival from EGIS. See{' '}
          <Link to="/guide/eta" hash="eta-comparison" className="underline underline-offset-2">Comparing EGIS and Kestrel</Link>{' '}
          for what each figure means.
        </p>
      </GuideSection>
    </GuideArticle>
  )
}
